/**
 * Get range for Y scale
 *
 * @param values values to get range for
 * @param defaultRange
 */
import { ITimelineItem, TTimelineRecord } from '../../types/vital-data';
import { IVitalDataSetting, TVitalRecordType } from '../../types/life-id';
import theme from '../../constants/theme';
import { IBarSegmentChartRange } from '../../components/bar-segment-chart/bar-segment-chart';
import { BMI_BAR_CHART_CONFIG } from '../../helpers/vital-list-helper/entries/bmi.entry.data';
import {
  WAIST_BAR_CHART_CONFIG,
  WAIST_BAR_CHART_CONFIG_FEMALE,
} from '../../helpers/vital-list-helper/entries/waist.entry.data';
import { GENDER_FEMALE, TGenderType } from '@hilbi/web-common/types/common';

export const axisOps = {
  axisLine: {
    show: false,
  },
  axisTick: {
    show: false,
  },
  splitLine: {
    show: true,
    lineStyle: {
      color: 'rgba(255,255,255,.1)',
    },
  },
};

export const getDataSeries = (data: any, index = 0, legend: any = {}, markArea?: any) => ({
  type: 'line',
  smooth: false,
  name: legend.text || '',
  lineStyle: {
    color: legend.color || theme.colors.success,
    width: 3,
  },
  itemStyle: {
    color: legend.color || theme.colors.success,
    opacity: 1,
  },
  symbolSize: 8,
  emphasis: {
    itemStyle: {
      borderWidth: 2,
      color: theme.colors.primary,
      borderColor: 'rgba(255,255,255,1)',
      opacity: 1,
    },
    symbolSize: 50,
  },
  data,
  markArea,
});

export const getYRange = (values: number[], defaultRange?: { min: number; max: number }) => {
  let min = defaultRange?.max || 99999;
  let max = defaultRange?.min || 0;

  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (+value < min) {
      min = +value;
    }
    if (+value > max) {
      max = +value;
    }
  }

  const range10Percent = (max - min) / 10;

  return { min: Math.floor(min - range10Percent), max: Math.ceil(max + range10Percent) };
};

function chartStateToColor(state: 'low' | 'lowered' | 'normal' | 'raised' | 'high' | 'unknown') {
  switch (state) {
    case 'normal':
      return 'rgba(106,229,159,1)';
    case 'raised':
    case 'lowered':
      return 'rgba(163,152,127,1)';
    case 'high':
    case 'low':
      return 'rgba(255,51,92,1)';
    default:
      return 'rgba(225,226,232,1)';
  }
}

interface GraphMarkArea {
  data: {
    yAxis: number;
    itemStyle: {
      color: string;
      opacity: number;
    };
  }[][];
}

/**
 * create extra/missing options for vital charts
 * @param settings
 * @param isMultiLine
 * @param values
 */
export function getExtraOptions(
  settings: IVitalDataSetting,
  isMultiLine: boolean,
  values?: ITimelineItem[],
) {
  const markArea: GraphMarkArea = {
    data: [],
  };

  const { minAvg, maxAvg } =
    isMultiLine || !values || values.length === 0
      ? {
          minAvg: settings.range.min,
          maxAvg: settings.range.max,
        }
      : getMinMaxAvg(values);

  if (isMultiLine) {
    return { markArea, minAvg, maxAvg };
  }

  const graphIndicatorStatesSorted = [...settings.graphIndicatorStates].sort(
    (a, b) => a.min - b.min,
  );

  let max = maxAvg;
  for (let i = 0; i < Object.values(graphIndicatorStatesSorted).length; i++) {
    const value = graphIndicatorStatesSorted[i];
    // @ts-ignore
    let color = chartStateToColor(value.state);

    if (i === 0) {
      for (let j = 0; j < graphIndicatorStatesSorted.length; j++) {
        const state = graphIndicatorStatesSorted[j];
        if (state.max > minAvg) {
          // @ts-ignore
          color = chartStateToColor(state.state);
          max = state.max;
          break;
        }
      }
      markArea.data.push([
        {
          yAxis: minAvg,
          itemStyle: {
            color: color,
            opacity: 0.4,
          },
        },
        {
          yAxis: max,
          itemStyle: {
            color: color,
            opacity: 0.4,
          },
        },
      ]);
    } else if (max < value.min) {
      markArea.data.push([
        {
          yAxis: value.min,
          itemStyle: {
            color: color,
            opacity: 0.4,
          },
        },
        {
          yAxis: value.max,
          itemStyle: {
            color: color,
            opacity: 0.4,
          },
        },
      ]);
    }
  }

  return { markArea, minAvg, maxAvg };
}

export function getMinMaxAvg(values?: ITimelineItem[]) {
  const res = {
    minAvg: 0,
    maxAvg: 0,
  };

  if (!values || values.length === 0) {
    return res;
  }

  const data = values
    .map((value) => {
      return parseInt(value.avg as any);
    })
    .sort(function (a, b) {
      return a - b;
    });

  // real avg
  res.maxAvg = data[data.length - 1];
  res.minAvg = data[0];

  // avg for padding in chart
  res.minAvg = res.minAvg - 1;
  res.maxAvg = res.maxAvg + 1;

  return res;
}

interface Series {
  value: any[];
  color: string;
  seriesName: string;
}

/**
 * Custom formatter for tooltip
 * @param _series series of data
 */
export const tooltipFormatter = (_series: Series[] | Series) => {
  const series = Array.isArray(_series) ? _series : [_series]; // fix for web: sometimes _series is object

  /**
   * dot indicator to show color of series
   * @param color color of series
   */
  const dotIndicator = (color: string) =>
    `<div style="width:10px; height:10px; border-radius: 10px; background-color:${color}; margin-right: 5px; border: 2px solid white;"></div>`;

  /**
   * Date of record
   */
  const date = new Date(series[0].value[0]);
  let result = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;

  /**
   * Go through all series and generate tooltip labels
   */
  // tslint:disable-next-line: prefer-for-of
  for (let i = 0; i < series.length; i++) {
    const lineData = series[i];
    result = `${result}<div style="display: flex; align-items: center;">${dotIndicator(
      lineData.color,
    )}${lineData.seriesName}: ${lineData.value[1]}</div>`;
  }

  return result;
};

function getExtraOptionsFromBarChartRanges(
  ranges: IBarSegmentChartRange[],
  values?: ITimelineItem[],
) {
  const { minAvg, maxAvg } = getMinMaxAvg(values);
  const markArea: any = {
    data: [],
  };

  let max = maxAvg;
  for (let i = 0; i < Object.values(ranges).length; i++) {
    const value = ranges[i];
    // @ts-ignore
    let color = '';

    if (i === 0) {
      for (let j = 0; j < ranges.length; j++) {
        const state = ranges[j];
        if (state.to > minAvg) {
          color = state.color;
          max = state.to;
          break;
        }
      }
      markArea.data.push([
        {
          yAxis: minAvg,
          itemStyle: {
            color: color,
            opacity: 0.4,
          },
        },
        {
          yAxis: max,
          itemStyle: {
            color: color,
            opacity: 0.4,
          },
        },
      ]);
    } else if (max <= value.from) {
      markArea.data.push([
        {
          yAxis: value.from,
          itemStyle: {
            color: value.color,
            opacity: 0.4,
          },
        },
        {
          yAxis: value.to,
          itemStyle: {
            color: value.color,
          },
        },
      ]);
    }
  }

  return { markArea, minAvg, maxAvg };
}

export function getOptionsFromBarChartConfig(
  data: any,
  legends: any,
  yRange: { min: number; max: number },
  type: TVitalRecordType,
  gender: TGenderType,
  timelineRecord?: TTimelineRecord,
) {
  let ranges: IBarSegmentChartRange[] = [];
  switch (type) {
    case 'BMI':
      ranges = BMI_BAR_CHART_CONFIG.ranges;
      break;
    case 'WAIST':
      ranges =
        gender === GENDER_FEMALE
          ? WAIST_BAR_CHART_CONFIG_FEMALE.ranges
          : WAIST_BAR_CHART_CONFIG.ranges;
      break;
  }

  const { markArea, minAvg, maxAvg } = getExtraOptionsFromBarChartRanges(
    ranges,
    timelineRecord?.values,
  );

  return {
    grid: {
      bottom: 40,
      top: 10,
      left: 15,
      right: 0,
      containLabel: true,
      borderColor: 'rgba(255,255,255,0.1)',
    },
    tooltip: {
      show: true,
      backgroundColor: 'rgba(51, 61, 108, 0.9)',
      borderWidth: 0,
      formatter: tooltipFormatter,
      trigger: 'axis',
      axisPointer: {
        type: 'line',
      },
    },
    xAxis: [
      {
        ...axisOps,
        type: 'time',
        axisLabel: {
          color: 'rgba(255,255,255,.4)',
          fontSize: 10,
          margin: 8,
          formatter: (value: any) => {
            const day = new Date(value).getDate();
            const month = new Date(value).getMonth();
            return `${day}.${month + 1}.`;
          },
        },
      },
      {
        show: true,
        silent: true,
        type: 'time',
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
    ],
    yAxis: {
      ...axisOps,
      ...yRange,
      min: minAvg,
      max: maxAvg,
      position: 'right',
      axisLabel: {
        fontSize: 12,
        fontWeight: 'bolder',
        color: '#fefefe',
        align: 'right',
        margin: 32,
      },
    },
    series: [
      ...(data.length > 0
        ? data.map((d: any, i: any) => getDataSeries(d, i, legends[i], markArea))
        : getDataSeries(data, 0, legends[0])),
    ],
  };
}

export function getLegendByDeclension(legend: any): string {
  switch (legend.declension) {
    case 'female':
      return 'life_id.average_female';
    case 'male':
      return 'life_id.average';
    case 'middle':
      return 'life_id.average_middle';
    default:
      throw new Error('Wrong label declension received');
  }
}
